import React, {useEffect, useState, useRef} from 'react';
import { useSearchParams, Link } from 'react-router-dom';

export const AuthRedirect = ({ config }) => {
  // 1. Add state for loading, error, and success data
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successData, setSuccessData] = useState(null);
  
  const [searchParams] = useSearchParams();

  // 1. Create a ref to track if the effect has already run.
  const effectRan = useRef(false);

  useEffect(() => {
    // 2. In Strict Mode, this check will be true on the second run.
    // In Production, this will never be true, but it's safe to keep.
    if (effectRan.current === true) {
      return; // Do nothing on the second run.
    }

    const processAuth = async () => {
      const code = searchParams.get("code");
      const state = searchParams.get("state");

      if (!code || !state) {
        setError("Missing required authentication parameters in the URL.");
        setIsLoading(false);
        return;
      }
      
      try {
        const { linkCode, chainId } = JSON.parse(decodeURIComponent(state));
        
        const backendUrl = config?.backendUrl || "http://localhost:8000";
        const response = await fetch(`${backendUrl}/discord/callback`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ 
            code,
            linkCode,
            chainId: parseInt(chainId)
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || `Request failed with status ${response.status}`);
        }

        const data = await response.json();
        setSuccessData(data);
        
      } catch (err) {
        console.log('Caught an error, setting state:', err.message);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    processAuth();

    // 3. Set the ref to true after the first run.
    // This will prevent the second run in Strict Mode.
    return () => {
      effectRan.current = true;
    };

  // The dependency array remains the same.
  }, [searchParams, config]); 


  // 5. Render UI based on the current state (loading, error, or success)
  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', marginTop: '5rem' }}>
        <h1>Linking your account...</h1>
        <p>Please wait while we securely connect to Discord.</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', marginTop: '5rem', color: 'red' }}>
        <h1>Authentication Failed</h1>
        <p>There was a problem linking your account:</p>
        <pre style={{ 
          backgroundColor: '#fdd', 
          padding: '10px', 
          borderRadius: '5px', 
          display: 'inline-block',
          color: '#333'
        }}>
          {error}
        </pre>
        <p>Please try again.</p>
      </div>
    );
  }

  if (successData) {
    return (
      <div style={{ textAlign: 'center', marginTop: '5rem', color: 'green' }}>
        <h1>Account Linked Successfully!</h1>
        <p>Welcome, <strong>{successData.user.discordUsername}</strong>!</p>
        <p>You can now close this window and return to Discord, or goto your dashboard.</p>
        <Link to="/dashboard">
          <button
            type="button"
            className="px-3 py-2 rounded bg-blue-300 dark:bg-blue-600 text-blue-900 dark:text-gray-100 hover:bg-blue-400 dark:hover:bg-blue-500"
          >
            Goto Dashboard
          </button>
        </Link>
      </div>
    );
  }

  // Fallback content, though it's unlikely to be shown with this logic
  return null;
};